import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import { Input } from 'components/FormFields';
import { DragIndicatorIcon } from 'components/Icon/DragIndicatorIcon';
import { Modal } from 'components/Modal';
import useTheme from 'hooks/useTheme';
import {
  CardWrapper,
  CardHeader,
  DragHandle,
  CardTitle,
  RaceName,
  RaceNamePlaceholder,
  SummaryBadges,
  Badge,
  PayoutCountBadge,
  ExpandButton,
  CardActions,
  ExpandedContent,
  ContentInner,
  FormGrid,
  SectionDivider,
  SectionTitle,
  SectionLine,
  PayoutRulesContainer,
  PayoutRuleCard,
  PayoutRuleNumber,
  PayoutFieldsRow,
  PayoutField,
  DeleteRuleButton,
  AddRuleButtonWrapper,
  AllocationSummary,
  AllocationLeft,
  AllocationText,
  AllocationProgress,
  AllocationProgressFill,
  AllocationStatus,
  PayoutEmptyState,
  PayoutEmptyText,
  ButtonWrapper,
  ModalHeader,
  ModalHeaderTitle,
  ModalCloseBtn,
  ModalBody,
  ModalText,
  ModalButtonRow,
} from './StyledComponents';

// Icons
const ChevronIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const LapsIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const PriceIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const PayoutIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const PlusIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const TotalIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="16" />
    <line x1="8" y1="12" x2="16" y2="12" />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const AlertIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const InfoIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

// Wrapper for action buttons (matches Notes tab style)
const ActionButtonWrapper = styled.div`
  button {
    width: 32px;
    height: 32px;
    padding: 0;
  }
`;

// Styled add button for payout rules
const AddRuleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => (theme.name === 'dark' ? '#94a3b8' : '#64748b')};
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.02)'};
  border: 1px dashed
    ${({ theme }) => (theme.name === 'dark' ? '#475569' : '#cbd5e1')};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ theme }) =>
      theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.1)'
        : 'rgba(250, 70, 22, 0.05)'};
    border-color: #fa4616;
    color: #fa4616;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Helper to check if a payout slot is active (not null)
// A slot is active if it was explicitly added, even if values are empty strings
const isSlotActive = (sponsorship, slotNum) => {
  const payout = sponsorship[`payout_${slotNum}`];
  // Slot is active if payout is not null (empty string is OK - means user is editing)
  return payout !== null && payout !== undefined;
};

// Helper to count payout rules
const countPayoutRules = (sponsorship) => {
  let count = 0;
  if (isSlotActive(sponsorship, 1)) count++;
  if (isSlotActive(sponsorship, 2)) count++;
  if (isSlotActive(sponsorship, 3)) count++;
  return count;
};

// Helper to get active payout rules as array
const getPayoutRules = (sponsorship) => {
  const rules = [];
  if (isSlotActive(sponsorship, 1)) {
    rules.push({
      index: 1,
      payout: sponsorship.payout_1,
      label: sponsorship.payout_1_label || '',
    });
  }
  if (isSlotActive(sponsorship, 2)) {
    rules.push({
      index: 2,
      payout: sponsorship.payout_2,
      label: sponsorship.payout_2_label || '',
    });
  }
  if (isSlotActive(sponsorship, 3)) {
    rules.push({
      index: 3,
      payout: sponsorship.payout_3,
      label: sponsorship.payout_3_label || '',
    });
  }
  return rules;
};

// Find next available slot
const getNextAvailableSlot = (sponsorship) => {
  if (!isSlotActive(sponsorship, 1)) return 1;
  if (!isSlotActive(sponsorship, 2)) return 2;
  if (!isSlotActive(sponsorship, 3)) return 3;
  return null;
};

// Get allocation info (total allocated vs total available)
const getAllocationInfo = (sponsorship) => {
  const totalAvailable = (sponsorship.num_laps || 0) * (sponsorship.price || 0);

  // Sum all payout amounts (treating empty strings and NaN as 0)
  let totalAllocated = 0;
  if (isSlotActive(sponsorship, 1)) {
    const val = parseFloat(sponsorship.payout_1);
    if (!isNaN(val)) totalAllocated += val;
  }
  if (isSlotActive(sponsorship, 2)) {
    const val = parseFloat(sponsorship.payout_2);
    if (!isNaN(val)) totalAllocated += val;
  }
  if (isSlotActive(sponsorship, 3)) {
    const val = parseFloat(sponsorship.payout_3);
    if (!isNaN(val)) totalAllocated += val;
  }

  const difference = totalAllocated - totalAvailable;
  let status = 'neutral';
  if (totalAvailable > 0) {
    if (difference > 0) status = 'over';
    else if (difference < 0) status = 'under';
    else if (totalAllocated === totalAvailable) status = 'exact';
  }

  const percent =
    totalAvailable > 0 ? (totalAllocated / totalAvailable) * 100 : 0;

  return {
    totalAvailable,
    totalAllocated,
    difference: Math.abs(difference),
    status,
    percent,
  };
};

const LapSponsorshipCard = ({
  sponsorship,
  expanded,
  onToggleExpand,
  onUpdate,
  onDelete,
}) => {
  const theme = useTheme();
  const isDark = theme?.name === 'dark';
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const payoutCount = countPayoutRules(sponsorship);
  const payoutRules = getPayoutRules(sponsorship);
  const allocation = getAllocationInfo(sponsorship);

  // Handle field changes
  const handleFieldChange = useCallback(
    (field, value) => {
      onUpdate({
        ...sponsorship,
        [field]: value,
      });
    },
    [sponsorship, onUpdate]
  );

  // Add a new payout rule
  const handleAddPayoutRule = useCallback(() => {
    const slot = getNextAvailableSlot(sponsorship);
    if (slot) {
      onUpdate({
        ...sponsorship,
        [`payout_${slot}`]: 0,
        [`payout_${slot}_label`]: '',
      });
    }
  }, [sponsorship, onUpdate]);

  // Delete a payout rule (clear the slot)
  const handleDeletePayoutRule = useCallback(
    (index) => {
      onUpdate({
        ...sponsorship,
        [`payout_${index}`]: null,
        [`payout_${index}_label`]: null,
      });
    },
    [sponsorship, onUpdate]
  );

  // Update payout rule
  const handlePayoutChange = useCallback(
    (index, field, value) => {
      const fieldName =
        field === 'payout' ? `payout_${index}` : `payout_${index}_label`;
      onUpdate({
        ...sponsorship,
        [fieldName]:
          field === 'payout' ? (value ? parseFloat(value) : '') : value,
      });
    },
    [sponsorship, onUpdate]
  );

  // Modal style
  const modalStyle = {
    backgroundColor: isDark ? '#1e293b' : '#ffffff',
    border: isDark ? '1px solid #334155' : 'none',
  };

  // Custom modal header
  const modalHeader = (
    <ModalHeader>
      <ModalHeaderTitle>Delete Lap Sponsorship</ModalHeaderTitle>
      <ModalCloseBtn onClick={() => setShowDeleteModal(false)}>
        <CloseIcon />
      </ModalCloseBtn>
    </ModalHeader>
  );

  return (
    <>
      <CardWrapper>
        <CardHeader onClick={onToggleExpand}>
          {/* Drag handle */}
          <DragHandle
            className="drag-handle"
            onClick={(e) => e.stopPropagation()}
            title="Drag to reorder"
          >
            <DragIndicatorIcon width={20} height={20} />
          </DragHandle>

          {/* Race name */}
          <CardTitle>
            <RaceName>
              {sponsorship.name || (
                <RaceNamePlaceholder>Untitled Race</RaceNamePlaceholder>
              )}
            </RaceName>
          </CardTitle>

          {/* Summary badges */}
          <SummaryBadges onClick={(e) => e.stopPropagation()}>
            <Badge>
              <LapsIcon />
              {sponsorship.num_laps || 0} laps
            </Badge>
            <Badge>
              <PriceIcon />${sponsorship.price || 0}/lap
            </Badge>
            <Badge>
              <TotalIcon />$
              {(
                (sponsorship.num_laps || 0) * (sponsorship.price || 0)
              ).toLocaleString()}{' '}
              total
            </Badge>
            <PayoutCountBadge count={payoutCount}>
              <PayoutIcon />
              {payoutCount} payout{payoutCount !== 1 ? 's' : ''}
            </PayoutCountBadge>
          </SummaryBadges>

          {/* Actions */}
          <CardActions onClick={(e) => e.stopPropagation()}>
            <ExpandButton
              type="button"
              expanded={expanded}
              onClick={onToggleExpand}
              title={expanded ? 'Collapse' : 'Expand'}
            >
              <ChevronIcon />
            </ExpandButton>
            <ActionButtonWrapper>
              <ButtonV2
                danger
                variant="icon"
                type="button"
                onClick={() => setShowDeleteModal(true)}
                title="Delete sponsorship"
                icon={<TrashIcon />}
              />
            </ActionButtonWrapper>
          </CardActions>
        </CardHeader>

        {/* Expanded content */}
        <ExpandedContent expanded={expanded}>
          <ContentInner>
            {/* Main fields */}
            <FormGrid>
              <Input
                label="Race Name"
                placeholder="e.g., Sportsman Race"
                value={sponsorship.name || ''}
                onChange={(e) => handleFieldChange('name', e.target.value)}
              />
              <Input
                label="Total Laps"
                type="number"
                placeholder="50"
                value={sponsorship.num_laps ?? ''}
                onChange={(e) =>
                  handleFieldChange(
                    'num_laps',
                    e.target.value ? parseInt(e.target.value, 10) : ''
                  )
                }
              />
              <Input
                label="Price per Lap"
                type="number"
                placeholder="10"
                value={sponsorship.price ?? ''}
                onChange={(e) =>
                  handleFieldChange(
                    'price',
                    e.target.value ? parseFloat(e.target.value) : ''
                  )
                }
                helperText="Price charged per lap"
              />
            </FormGrid>

            {/* Payout Distribution section */}
            <SectionDivider>
              <SectionTitle>Payout Distribution</SectionTitle>
              <SectionLine />
            </SectionDivider>

            <PayoutRulesContainer>
              {payoutRules.length === 0 ? (
                <PayoutEmptyState>
                  <PayoutEmptyText>
                    No payout rules configured. Add rules to define how
                    sponsorship funds are distributed.
                  </PayoutEmptyText>
                  <ButtonWrapper>
                    <ButtonV2 type="button" onClick={handleAddPayoutRule}>
                      <PlusIcon style={{ marginRight: 8 }} />
                      Add Payout Rule
                    </ButtonV2>
                  </ButtonWrapper>
                </PayoutEmptyState>
              ) : (
                <>
                  {payoutRules.map((rule, idx) => (
                    <PayoutRuleCard key={rule.index}>
                      <PayoutRuleNumber>{idx + 1}</PayoutRuleNumber>
                      <PayoutFieldsRow>
                        <PayoutField flex={2}>
                          <Input
                            label="Criteria"
                            placeholder="e.g., Leader at halfway"
                            value={rule.label}
                            onChange={(e) =>
                              handlePayoutChange(
                                rule.index,
                                'label',
                                e.target.value
                              )
                            }
                          />
                        </PayoutField>
                        <PayoutField flex={1}>
                          <Input
                            label="Payout ($)"
                            type="number"
                            placeholder="100"
                            value={rule.payout ?? ''}
                            onChange={(e) =>
                              handlePayoutChange(
                                rule.index,
                                'payout',
                                e.target.value
                              )
                            }
                          />
                        </PayoutField>
                      </PayoutFieldsRow>
                      <DeleteRuleButton
                        type="button"
                        onClick={() => handleDeletePayoutRule(rule.index)}
                        title="Remove rule"
                      >
                        <TrashIcon />
                      </DeleteRuleButton>
                    </PayoutRuleCard>
                  ))}

                  {/* Allocation Summary */}
                  {allocation.totalAvailable > 0 && (
                    <AllocationSummary status={allocation.status}>
                      <AllocationLeft>
                        <AllocationText>
                          Allocated:{' '}
                          <span>
                            ${allocation.totalAllocated.toLocaleString()}
                          </span>{' '}
                          of{' '}
                          <span>
                            ${allocation.totalAvailable.toLocaleString()}
                          </span>
                        </AllocationText>
                        <AllocationProgress>
                          <AllocationProgressFill
                            percent={allocation.percent}
                            status={allocation.status}
                          />
                        </AllocationProgress>
                      </AllocationLeft>
                      <AllocationStatus status={allocation.status}>
                        {allocation.status === 'exact' && (
                          <>
                            <CheckIcon /> Fully allocated
                          </>
                        )}
                        {allocation.status === 'over' && (
                          <>
                            <AlertIcon /> $
                            {allocation.difference.toLocaleString()} over budget
                          </>
                        )}
                        {allocation.status === 'under' && (
                          <>
                            <InfoIcon /> $
                            {allocation.difference.toLocaleString()} remaining
                          </>
                        )}
                        {allocation.status === 'neutral' && (
                          <>
                            ${allocation.difference.toLocaleString()} remaining
                          </>
                        )}
                      </AllocationStatus>
                    </AllocationSummary>
                  )}

                  {payoutCount < 3 && (
                    <AddRuleButtonWrapper>
                      <AddRuleButton
                        type="button"
                        onClick={handleAddPayoutRule}
                      >
                        <PlusIcon />
                        Add Payout Rule
                      </AddRuleButton>
                    </AddRuleButtonWrapper>
                  )}
                </>
              )}
            </PayoutRulesContainer>
          </ContentInner>
        </ExpandedContent>
      </CardWrapper>

      {/* Delete confirmation modal */}
      <Modal
        isVisible={showDeleteModal}
        hideModal={() => setShowDeleteModal(false)}
        maxWidth={450}
        modalStyle={modalStyle}
        header={modalHeader}
      >
        <ModalBody>
          <ModalText>
            Are you sure you want to delete "
            {sponsorship.name || 'this lap sponsorship'}"? This action cannot be
            undone.
          </ModalText>
          <ModalButtonRow>
            <ButtonWrapper>
              <ButtonV2 type="button" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </ButtonV2>
            </ButtonWrapper>
            <ButtonWrapper>
              <ButtonV2
                elevated
                type="button"
                onClick={() => {
                  onDelete();
                  setShowDeleteModal(false);
                }}
              >
                Delete
              </ButtonV2>
            </ButtonWrapper>
          </ModalButtonRow>
        </ModalBody>
      </Modal>
    </>
  );
};

export default LapSponsorshipCard;
